import { callApi } from "../api/fetch";
import { TEXTS } from "../components/newOnboarding/constants/texts";

export type PasswordResetRedirectParams = {
  resetToken: string;
  error: string;
  status: number;
};

export function getPasswordResetRedirects() {
  const origin = window.location.origin;
  return {
    successRedirect: `${origin}/auth/reset-password`,
    failureRedirect: `${origin}/password-reset-link-expiry`,
  };
}

export async function sendPasswordResetLink(email: string) {
  const { successRedirect, failureRedirect } = getPasswordResetRedirects();

  return callApi("/user/password-reset", {
    method: "POST",
    data: {
      email,
      success_redirect: successRedirect,
      failure_redirect: failureRedirect,
    },
  });
}

export async function resetUserPassword(resetToken: string, password: string) {
  return callApi("/user/password-reset/confirm", {
    method: "POST",
    data: {
      reset_token: resetToken,
      password,
    },
  });
}

export function parsePasswordResetRedirect(
  search: string
): PasswordResetRedirectParams {
  const params = new URLSearchParams(search);
  const rawError = params.get("err");
  // The backend redirect interpolates `err=${err}`, so on success it sends the
  // literal string "null"/"undefined". Treat those as "no error".
  const error =
    rawError && rawError !== "null" && rawError !== "undefined" ? rawError : "";
  const status = Number(params.get("status") || "0");
  let resetToken = "";

  const rawData = params.get("data");
  if (rawData) {
    try {
      const parsed = JSON.parse(rawData) as {
        reset_token?: string;
        resetToken?: string;
      };
      resetToken = parsed.reset_token || parsed.resetToken || "";
    } catch {
      // ignore malformed redirect payload
    }
  }

  return { resetToken, error, status };
}

export function isPasswordResetRedirectInvalid(
  params: PasswordResetRedirectParams
): boolean {
  if (params.error) return true;
  if (params.status >= 400) return true;
  return false;
}

/** User-safe message for password-reset link request failures (hides internal validation text). */
export function getPasswordResetRequestErrorMessage(err: unknown): string {
  const message = ((err as Error)?.message || "").toLowerCase();
  if (
    message.includes("success_redirect") ||
    message.includes("failure_redirect") ||
    message.includes("invalid redirect")
  ) {
    return TEXTS.GENERIC_ERROR_MESSAGE;
  }
  return (err as Error)?.message || TEXTS.GENERIC_ERROR_MESSAGE;
}
